import { useState } from "react";
import { photoUrl, api, formatApiError } from "@/lib/api";
import { Heart, Trash2, Sparkles, Loader2, Check, Star } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import TagEditor from "@/components/TagEditor";
import { PHOTO, PET } from "@/constants/testIds";

const ROTATIONS = ["-rotate-2", "-rotate-1", "rotate-0", "rotate-1", "rotate-2"];

export default function PhotoCard({
  photo,
  index = 0,
  onUpdate,
  onDelete,
  pets = [],
  selectionMode = false,
  selected = false,
  onToggleSelect,
  onCoverChanged,
}) {
  const [open, setOpen] = useState(false);
  const [p, setP] = useState(photo);
  const rotation = ROTATIONS[index % ROTATIONS.length];
  const pet = pets.find((pt) => pt.id === p.pet_id);

  const patch = async (fields) => {
    try {
      const { data } = await api.patch(`/photos/${p.id}`, fields);
      setP(data);
      onUpdate?.(data);
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleFav = async (e) => {
    e?.stopPropagation();
    await patch({ is_favorite: !p.is_favorite });
  };

  const del = async (e) => {
    e?.stopPropagation();
    if (!confirm("Delete this photo?")) return;
    try {
      await api.delete(`/photos/${p.id}`);
      toast.success("Photo removed");
      onDelete?.(p.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const reAnalyze = async () => {
    try {
      await api.post(`/photos/${p.id}/analyze`);
      toast.success("Re-analyzing breed...");
      setP({ ...p, ai_status: "pending" });
      setTimeout(async () => {
        try {
          const { data } = await api.get(`/photos/${p.id}`);
          setP(data);
          onUpdate?.(data);
        } catch (e) { void e; }
      }, 4000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const setAsCover = async () => {
    if (!pet) return;
    try {
      await api.patch(`/pets/${pet.id}/cover`, { photo_id: p.id });
      toast.success(`Featured photo set for ${pet.name}`);
      onCoverChanged?.(pet.id, p.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleClick = (e) => {
    if (selectionMode) {
      e.preventDefault();
      onToggleSelect?.(p.id);
    } else {
      setOpen(true);
    }
  };

  return (
    <>
      <div
        data-testid={PHOTO.card}
        onClick={handleClick}
        className={`polaroid group cursor-pointer break-inside-avoid mb-6 ${rotation} hover:z-10 ${selected ? "ring-4 ring-[#E76F51] ring-offset-2 ring-offset-[#FDFBF7]" : ""}`}
      >
        <div className="relative overflow-hidden rounded-sm bg-[#F4EFE6]">
          <img
            src={photoUrl(p.id)}
            alt={p.caption || p.original_filename}
            className="w-full h-auto block"
            loading="lazy"
          />
          {selectionMode && (
            <div
              data-testid={PHOTO.selectToggle}
              className={`absolute top-2 right-2 w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${
                selected ? "bg-[#E76F51] border-[#E76F51]" : "bg-white/90 border-white"
              }`}
            >
              {selected && <Check className="w-4 h-4 text-white" />}
            </div>
          )}
          {!selectionMode && (
            <button
              data-testid={PHOTO.favoriteBtn}
              onClick={toggleFav}
              className="absolute top-2 right-2 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:scale-110"
            >
              <Heart className={`w-4 h-4 ${p.is_favorite ? "fill-[#E76F51] text-[#E76F51]" : "text-[#2D232E]"}`} />
            </button>
          )}
          {p.is_favorite && !selectionMode && (
            <div className="absolute top-2 left-2 w-8 h-8 rounded-full bg-[#E9C46A] flex items-center justify-center">
              <Heart className="w-4 h-4 fill-white text-white" />
            </div>
          )}
          {p.ai_status === "done" && p.ai_breed && (
            <div className="absolute bottom-2 left-2 bg-[#2D232E]/80 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              {p.ai_breed}
            </div>
          )}
        </div>
        <div className="pt-3 px-1 flex items-center justify-between">
          <div className="font-hand text-xl text-[#2D232E] leading-tight truncate">
            {p.caption || pet?.name || "untitled"}
          </div>
          {pet && (
            <span
              className="text-xs font-semibold px-2 py-0.5 rounded-full ml-2 shrink-0"
              style={{ backgroundColor: `${pet.color}22`, color: pet.color }}
            >
              {pet.name}
            </span>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl rounded-3xl bg-[#FDFBF7] border-[#EAE2D6] p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
          <DialogTitle className="sr-only">{p.caption || "Photo details"}</DialogTitle>
          <div className="grid md:grid-cols-5">
            <div className="md:col-span-3 bg-[#2D232E] flex items-center justify-center">
              <img src={photoUrl(p.id)} alt={p.caption || ""} className="w-full h-auto max-h-[70vh] object-contain" />
            </div>
            <div className="md:col-span-2 p-6 space-y-4">
              <div>
                <div className="font-hand text-2xl text-[#E76F51] mb-1">{p.caption || "a lovely moment"}</div>
                <div className="text-sm text-[#6A606C]">
                  {new Date(p.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                </div>
              </div>

              {pet && (
                <div className="flex items-center justify-between gap-2 p-3 rounded-2xl bg-white border border-[#EAE2D6]">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-full shrink-0" style={{ backgroundColor: pet.color }} />
                    <div className="min-w-0">
                      <div className="font-semibold text-[#2D232E] truncate">{pet.name}</div>
                      <div className="text-xs text-[#6A606C] capitalize">{pet.species}</div>
                    </div>
                  </div>
                  {pet.cover_photo_id === p.id ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#E9C46A] bg-[#E9C46A]/15 px-2.5 py-1 rounded-full shrink-0">
                      <Star className="w-3.5 h-3.5 fill-[#E9C46A]" />
                      Featured
                    </span>
                  ) : (
                    <button
                      data-testid={PET.setCoverBtn}
                      onClick={setAsCover}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#6A606C] hover:text-[#E76F51] bg-[#F4EFE6] hover:bg-white border border-transparent hover:border-[#E76F51]/40 px-2.5 py-1 rounded-full shrink-0 transition-colors"
                    >
                      <Star className="w-3.5 h-3.5" />
                      Set as cover
                    </button>
                  )}
                </div>
              )}

              <div className="rounded-2xl bg-white border border-[#EAE2D6] p-4">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-[#2A9D8F] font-bold mb-2">
                  <Sparkles className="w-3.5 h-3.5" />
                  AI Breed Detection
                </div>
                {p.ai_status === "pending" && (
                  <div className="flex items-center gap-2 text-[#6A606C]">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analyzing this cutie...
                  </div>
                )}
                {p.ai_status === "done" && (
                  <>
                    <div className="font-display text-xl text-[#2D232E] font-semibold">
                      {p.ai_breed || "Unknown"}
                      {p.ai_confidence != null && (
                        <span className="ml-2 text-sm font-medium text-[#2A9D8F]">{p.ai_confidence}% sure</span>
                      )}
                    </div>
                    {p.ai_description && <p className="text-sm text-[#6A606C] mt-1">{p.ai_description}</p>}
                  </>
                )}
                {p.ai_status === "failed" && (
                  <div className="text-sm text-[#6A606C]">Couldn&apos;t analyze this one.</div>
                )}
                <button
                  data-testid={PHOTO.reanalyzeBtn}
                  onClick={reAnalyze}
                  disabled={p.ai_status === "pending"}
                  className="mt-3 text-sm text-[#E76F51] font-semibold hover:underline"
                >
                  Re-analyze
                </button>
              </div>

              <TagEditor tags={p.tags || []} onChange={(tags) => patch({ tags })} />

              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={toggleFav}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full font-semibold text-sm transition-all ${
                    p.is_favorite ? "bg-[#E76F51] text-white" : "bg-white border border-[#EAE2D6] text-[#2D232E] hover:border-[#E76F51]"
                  }`}
                >
                  <Heart className={`w-4 h-4 ${p.is_favorite ? "fill-white" : ""}`} />
                  {p.is_favorite ? "Favorited" : "Favorite"}
                </button>
                <button
                  data-testid={PHOTO.deleteBtn}
                  onClick={del}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full font-semibold text-sm bg-white border border-[#EAE2D6] text-[#6A606C] hover:text-red-600 hover:border-red-300"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
